import { useFetch } from "~vue/api";
import { i18n } from "~vue/i18n";
import type { DecoratedPerformanceReview, PerformanceReviewConversation } from "~vue/types/performance_management";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { ref } from "vue";

/** The launch-failure message, translated at call time so it follows the active UI language. */
export const startConversationErrorMessage = (): string => i18n.global.t("moments.page.startReviewError");

/**
 * Starts (or idempotently re-opens) the chat for a performance review and returns its URL.
 * Mirrors PerformanceMomentEndOfYear.startPerformanceReviewConversation; the backend returns the
 * existing non-completed conversation when one already exists, so double-clicks never seed twins.
 *
 * Failures resolve to null with `launchError` set rather than rejecting, so every caller
 * surfaces the same message instead of each needing its own catch — navigation is the only
 * success path, and a silent no-op leaves the user staring at an unchanged screen. The one
 * exception is a session-ended failure, where the dialog already owns the message and a toast
 * beside it would contradict it.
 */
export function useStartPerformanceReviewConversation() {
    const isLaunching = ref(false);
    const launchError = ref("");

    const conversationPayload = ref<{ relationship_id: string | null; performance_moment_cta_id: number; source_session_uuid?: string } | null>(null);
    const {
        data,
        execute,
        statusCode,
        error: fetchError,
    } = useFetch("/api/coach/performance_reviews/conversations/", { immediate: false }).post(conversationPayload).json<PerformanceReviewConversation>();

    const startConversation = async (review: DecoratedPerformanceReview, sourceSessionUuid?: string): Promise<string | null> => {
        if (isLaunching.value) {
            return null;
        }
        isLaunching.value = true;
        launchError.value = "";

        const errorLogFields = (httpStatus: number | null | undefined) => ({
            tags: { feature: "performance_management", action: "start_review_conversation" },
            context: {
                relationship_id: review.performance_review_relationship_id,
                cta_id: review.performance_moment_cta_id,
                http_status: httpStatus ?? null,
            },
        });

        // Fire-and-forget, before the POST: if the launch dies (e.g. a stale CSRF token), this is
        // the only record that the user clicked at all.
        void logUserInteraction(AmplitudeEventNames.PERFORMANCE_REVIEW_START_REVIEW_CTA_CLICKED, {
            cta_id: review.performance_moment_cta_id,
            relationship_id: review.performance_review_relationship_id,
        });

        try {
            conversationPayload.value = {
                relationship_id: review.performance_review_relationship_id,
                performance_moment_cta_id: review.performance_moment_cta_id,
                ...(sourceSessionUuid ? { source_session_uuid: sourceSessionUuid } : {}),
            };
            await execute(true);
            const url = data.value?.coaching_session_url ?? null;
            // A 2xx with no URL means the idempotency guard handed back a concurrent claim whose
            // chat is still materializing (see CreatePerformanceReviewConversationApi). There is
            // nowhere to navigate, so it is a failure from the user's point of view.
            if (!url) {
                logError(new Error("Performance review conversation launch returned no URL"), errorLogFields(statusCode.value));
                launchError.value = startConversationErrorMessage();
            }
            return url;
        } catch (error) {
            // execute(true) rejects with the transport error, not the one the fetch wrapper's
            // onFetchError substituted; the substituted error lands on the error ref instead.
            if (isSessionEndedError(error) || isSessionEndedError(fetchError.value)) {
                return null;
            }
            logError(error, errorLogFields(statusCode.value));
            launchError.value = startConversationErrorMessage();
            return null;
        } finally {
            isLaunching.value = false;
        }
    };

    return { isLaunching, launchError, startConversation };
}
